import React, { useCallback, useEffect, useRef, useState } from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from './rn';
import { radius, spacing } from '../theme';
import { useTheme } from '../theme/ThemeContext';
import { useTranslation } from '../i18n/I18nContext';

/**
 * `expo-audio` NATIVE bir modül; ikiliye ancak derlemeyle girer. Güncelleme
 * yalnız JS taşıdığı için bu dosya modülü taşımayan eski bir ikiliye de
 * düşebiliyor (D-568'de video için aynısı yaşandı). Üst seviye `import`
 * bırakılsaydı o ikilide mesaj listesi bozulurdu.
 *
 * Oynatıcı FABRİKA ile alınıyor (`createAudioPlayer`), kanca ile değil: kanca
 * kullanmak, modülün varlığına bağlı bir kanca çağrısı demek olurdu. D-567'de
 * kayıt tarafında paketin gerçekten neyi dışa verdiğine bakmanın önemi
 * görülmüştü; buradaki ad da `expoAudioSurface.test.ts` ile pakete bağlı.
 */
type AnyPlayer = {
  play(): void;
  pause(): void;
  seekTo(seconds: number): Promise<void> | void;
  remove(): void;
  currentTime?: number;
  duration?: number;
  playing?: boolean;
};
type AudioModule = {
  createAudioPlayer?: (source: unknown) => AnyPlayer;
};

function yukle(): AudioModule | null {
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    return require('expo-audio') as AudioModule;
  } catch {
    return null;
  }
}

const modulRef = yukle();

export const voicePlaybackAvailable = typeof modulRef?.createAudioPlayer === 'function';

/** Kaydın süresi; oynarken geçen süre, dururken toplam süre gösterilir. */
export function formatVoiceTime(seconds: number | null | undefined): string {
  const tam = typeof seconds === 'number' && Number.isFinite(seconds) && seconds > 0
    ? Math.round(seconds) : 0;
  return `${Math.floor(tam / 60)}:${String(tam % 60).padStart(2, '0')}`;
}

type Props = {
  url: string;
  mine: boolean;
  durationSeconds?: number | null;
  onLongPress: () => void;
  /** Testte gerçek native oynatıcı yerine sahtesi geçilebilsin diye. */
  createPlayer?: (source: unknown) => AnyPlayer;
};

/**
 * Sohbetteki ses mesajı (9.1.7 · D-570).
 *
 * **Kendiliğinden oynamıyor.** Listeyi kaydırmak birkaç kaydı birden
 * indirmeye başlatırdı; video için verilen karar (D-563) burada da geçerli,
 * üstelik ses mesajı ücretli bir şeyin karşılığı — sahibinin haberi olmadan
 * çalması doğru olmaz.
 *
 * Oynatıcı ilk DOKUNUŞTA doğuyor, çizimde değil: çizilen her balon için bir
 * native nesne açmak, uzun bir sohbette onlarca açık dosya demekti.
 */
export default function MessageVoice({
  url, mine, durationSeconds, onLongPress, createPlayer,
}: Props) {
  const colors = useTheme();
  const { t } = useTranslation();
  const [playing, setPlaying] = useState(false);
  const [position, setPosition] = useState(0);
  const oynatici = useRef<AnyPlayer | null>(null);
  const sayac = useRef<ReturnType<typeof setInterval> | null>(null);

  const toplam = typeof durationSeconds === 'number' && durationSeconds > 0 ? durationSeconds : 0;

  const sayaciDurdur = useCallback(() => {
    if (sayac.current) { clearInterval(sayac.current); sayac.current = null; }
  }, []);

  // Ekrandan çıkan balon native nesnesini bırakıyor; bırakılmazsa uzun bir
  // sohbet açık dosya biriktirir.
  useEffect(() => () => {
    sayaciDurdur();
    oynatici.current?.remove();
    oynatici.current = null;
  }, [sayaciDurdur]);

  function duraklat() {
    oynatici.current?.pause();
    sayaciDurdur();
    setPlaying(false);
  }

  function bastanBasla() {
    // Kayıt bitince SON saniyede duruyor; başa sarmadan oynatmak hiçbir şey
    // yapmazdı. Video tarafında bu tam olarak böyle kaçmıştı (D-564).
    void oynatici.current?.seekTo(0);
    setPosition(0);
  }

  function oynat() {
    const yapici = createPlayer ?? modulRef?.createAudioPlayer;
    if (!yapici) return;
    if (!oynatici.current) oynatici.current = yapici({ uri: url });
    const p = oynatici.current;
    const uzunluk = p.duration || toplam;
    if (uzunluk > 0 && (p.currentTime ?? 0) >= uzunluk - 0.25) bastanBasla();
    p.play();
    setPlaying(true);
    sayaciDurdur();
    sayac.current = setInterval(() => {
      const aygit = oynatici.current;
      if (!aygit) return;
      const gecen = aygit.currentTime ?? 0;
      setPosition(gecen);
      const son = aygit.duration || toplam;
      if (son > 0 && gecen >= son - 0.25) {
        // Bitti: durdurulup başa alınıyor, ama kendiliğinden tekrar OYNAMIYOR.
        aygit.pause();
        sayaciDurdur();
        setPlaying(false);
        setPosition(0);
        void aygit.seekTo(0);
      }
    }, 250);
  }

  if (!voicePlaybackAvailable && !createPlayer) {
    return (
      <TouchableOpacity testID="message-voice" activeOpacity={0.9} onLongPress={onLongPress}>
        <View style={[styles.row, styles.fallback]}>
          <Text testID="message-voice-unavailable" style={[styles.fallbackText, { color: colors.gray600 }]}>
            {t('message.voiceNeedsUpdate')}
          </Text>
        </View>
      </TouchableOpacity>
    );
  }

  // Duraklatınca toplam süreye dönmüyor: kaldığı yeri gösteriyor. Dönseydi,
  // duraklatmak kaydı başa sarmış gibi görünürdü — cihazda tam olarak böyle
  // göründüğü için düzeltildi. Hiç oynatılmamış veya bitmiş kayıtta toplam
  // süre yazar, çünkü orada anlamlı olan sayı odur.
  const gosterilen = position > 0 ? position : toplam;
  const oran = toplam > 0 ? Math.min(1, Math.max(0, position / toplam)) : 0;
  const vurgu = mine ? colors.white : colors.black;

  return (
    <TouchableOpacity
      testID="message-voice"
      activeOpacity={0.9}
      onPress={() => (playing ? duraklat() : oynat())}
      onLongPress={onLongPress}>
      <View style={styles.row}>
        <View testID="message-voice-button" style={[styles.badge, { borderColor: vurgu }]}>
          <Text style={[styles.glyph, { color: vurgu }]}>{playing ? '❙❙' : '▶'}</Text>
        </View>
        <View style={styles.track}>
          <View style={[styles.trackBase, { backgroundColor: vurgu, opacity: 0.25 }]} />
          <View
            testID="message-voice-progress"
            style={[styles.trackFill, { backgroundColor: vurgu, width: `${oran * 100}%` }]}
          />
        </View>
        <Text testID="message-voice-time" style={[styles.time, { color: vurgu }]}>
          {formatVoiceTime(gosterilen)}
        </Text>
      </View>
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  row: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, minWidth: 180 },
  badge: {
    width: 32, height: 32, borderRadius: 16, borderWidth: 1.5,
    alignItems: 'center', justifyContent: 'center',
  },
  glyph: { fontSize: 12 },
  track: { flex: 1, height: 3, justifyContent: 'center' },
  trackBase: { position: 'absolute', left: 0, right: 0, height: 3, borderRadius: radius.control },
  trackFill: { height: 3, borderRadius: radius.control },
  time: { fontSize: 12, fontVariant: ['tabular-nums'] },
  fallback: { paddingVertical: spacing.xs },
  fallbackText: { fontSize: 13 },
});
